import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  X,
  Upload,
  Search,
  Loader2,
  AlertCircle,
  ImageIcon,
  ArrowUp,
  ArrowDown,
  Armchair,
  Table as TableIcon,
  Layers,
  Ruler,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { productsApi, categoriesApi } from '@/services/api';
import type { Product, Category, PaginatedResponse, ProductColor } from '@/types';
import { HexColorPicker } from 'react-colorful';
import { toast } from 'sonner';

// Mobilya üretiminde kullanılan malzemeler
const MATERIAL_OPTIONS: Record<string, string[]> = {
  'Ahşap': ['Meşe', 'Ceviz', 'Kayın', 'Gürgen', 'Çam', 'Akçaağaç', 'Maun', 'Masif Ahşap', 'MDF', 'Sunta', 'Kontrplak'],
  'Metal': ['Çelik', 'Demir', 'Alüminyum', 'Krom', 'Pirinç'],
  'Kumaş': ['Pamuk', 'Keten', 'Kadife', 'Mikrofiber', 'Polyester'],
  'Deri': ['Gerçek Deri', 'Suni Deri (PU Deri)'],
  'Diğer': ['Cam', 'Plastik', 'Mermer', 'Granit', 'Hasır', 'Rattan'],
};

interface FormData {
  title: string;
  slug: string;
  description: string;
  categoryIds: string[];
  hidden: boolean;
  featured: boolean;
  dimensions: {
    width: string;
    height: string;
    depth: string;
    seatHeight: string;
    tableHeight: string;
    unit: string;
    // For Sets: Separate table and chair dimensions
    tableDimensions: {
      width: string;
      height: string;
      depth: string;
      tableHeight: string;
    };
    chairDimensions: {
      width: string;
      height: string;
      depth: string;
      seatHeight: string;
    };
  };
  technicalSpecs: {
    productType: 'chair' | 'table' | 'set';
    capacity: string;
    weight: string;
    usageArea: string;
    setContent: string;
    tableCount: number;
    chairCount: number;
  };
  warranty: string;
  materials: string;
  colors: ProductColor[];
  similarProductIds: string[];
}

const initialFormData: FormData = {
  title: '',
  slug: '',
  description: '',
  categoryIds: [],
  hidden: false,
  featured: false,
  dimensions: {
    width: '', height: '', depth: '', seatHeight: '', tableHeight: '', unit: 'cm',
    tableDimensions: { width: '', height: '', depth: '', tableHeight: '' },
    chairDimensions: { width: '', height: '', depth: '', seatHeight: '' },
  },
  technicalSpecs: { productType: 'chair', capacity: '', weight: '', usageArea: '', setContent: '', tableCount: 0, chairCount: 0 },
  warranty: '2 Yıl',
  materials: '',
  colors: [],
  similarProductIds: [],
};

export const ProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [productMeta, setProductMeta] = useState<PaginatedResponse<Product>['meta'] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [similarSearch, setSimilarSearch] = useState('');
  const [activeColorPicker, setActiveColorPicker] = useState<number | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [creationStep, setCreationStep] = useState<'type-selection' | 'form'>('form');
  const [materialsDropdownOpen, setMaterialsDropdownOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<{ id: string; url: string; isPrimary: boolean }[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploadingColorIndex, setUploadingColorIndex] = useState<number | null>(null);

  const selectedSimilarProducts = useMemo(() => {
    return formData.similarProductIds
      .map((id) => products.find((product) => product.id === id))
      .filter((product): product is Product => Boolean(product));
  }, [formData.similarProductIds, products]);

  // Seçili malzemeler (virgülle ayrılmış string'den array'e)
  const selectedMaterials = useMemo(() => {
    return formData.materials.split(',').map(m => m.trim()).filter(Boolean);
  }, [formData.materials]);

  // Malzeme seçim toggle fonksiyonu
  const toggleMaterial = useCallback((material: string) => {
    const materials = [...selectedMaterials];
    const index = materials.indexOf(material);
    if (index > -1) {
      materials.splice(index, 1);
    } else {
      materials.push(material);
    }
    setFormData(prev => ({ ...prev, materials: materials.join(', ') }));
  }, [selectedMaterials]);

  const availableSimilarProducts = useMemo(() => {
    const normalizedSearch = similarSearch.trim().toLowerCase();

    return products
      .filter((product) => product.id !== editingProduct?.id)
      .filter((product) => !formData.similarProductIds.includes(product.id))
      .filter((product) => {
        if (!normalizedSearch) {
          return true;
        }
        return (
          product.title.toLowerCase().includes(normalizedSearch) ||
          product.slug.toLowerCase().includes(normalizedSearch)
        );
      })
      .slice(0, 15);
  }, [products, editingProduct, formData.similarProductIds, similarSearch]);

  useEffect(() => {
    loadData();
  }, []);

  // Cleanup image previews on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        productsApi.getAdminList({ page, limit: 100 }),
        categoriesApi.getAll(),
      ]);
      setProducts(productsData.data || []);
      setProductMeta(productsData.meta ?? null);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = useCallback((title: string) => {
    return title
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }, []);

  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: editingProduct ? prev.slug : generateSlug(title),
    }));
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      ...initialFormData,
      dimensions: { ...initialFormData.dimensions },
      colors: [],
      similarProductIds: [],
    });
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    setFormError('');
    setSimilarSearch('');
    setCreationStep('type-selection');
    setModalOpen(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      slug: product.slug,
      description: product.description || '',
      categoryIds: product.categories?.map(c => c.id) || [],
      hidden: product.hidden,
      featured: product.featured,
      dimensions: {
        width: product.dimensions?.width?.toString() || '',
        height: product.dimensions?.height?.toString() || '',
        depth: product.dimensions?.depth?.toString() || '',
        seatHeight: product.dimensions?.seatHeight?.toString() || '',
        tableHeight: product.dimensions?.tableHeight?.toString() || '',
        unit: product.dimensions?.unit || 'cm',
        tableDimensions: {
          width: (product.dimensions as any)?.tableDimensions?.width?.toString() || '',
          height: (product.dimensions as any)?.tableDimensions?.height?.toString() || '',
          depth: (product.dimensions as any)?.tableDimensions?.depth?.toString() || '',
          tableHeight: (product.dimensions as any)?.tableDimensions?.tableHeight?.toString() || '',
        },
        chairDimensions: {
          width: (product.dimensions as any)?.chairDimensions?.width?.toString() || '',
          height: (product.dimensions as any)?.chairDimensions?.height?.toString() || '',
          depth: (product.dimensions as any)?.chairDimensions?.depth?.toString() || '',
          seatHeight: (product.dimensions as any)?.chairDimensions?.seatHeight?.toString() || '',
        },
      },
      technicalSpecs: {
        productType: product.technicalSpecs?.productType || 'chair',
        capacity: (product.technicalSpecs as any)?.capacity || '',
        weight: (product.technicalSpecs as any)?.weight || '',
        usageArea: product.technicalSpecs?.usageArea || '',
        setContent: (product.technicalSpecs as any)?.setContent || '',
        tableCount: (product.technicalSpecs as any)?.tableCount || 0,
        chairCount: (product.technicalSpecs as any)?.chairCount || 0,
      },
      warranty: product.warranty || '2 Yıl',
      materials: product.materials?.join(', ') || '',
      colors: (product.colors || []).map((color) => ({ ...color })),
      similarProductIds: [...(product.similarProductIds || [])],
    });
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages(product.images?.map(img => ({
      id: img.id,
      url: img.url,
      isPrimary: img.isPrimary,
    })) || []);
    setFormError('');
    setSimilarSearch('');
    setCreationStep('form');
    setModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      dimensions: { ...initialFormData.dimensions },
      colors: [],
      similarProductIds: [],
    });
    setImageFiles([]);
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setImagePreviews([]);
    setExistingImages([]);
    setEditingProduct(null);
    setFormError('');
    setSimilarSearch('');
  };

  const handleAddColor = () => {
    setFormData((prev) => ({
      ...prev,
      colors: [...prev.colors, { name: '', hex: '', image: '' }],
    }));
  };

  const handleColorChange = (
    index: number,
    key: keyof Pick<ProductColor, 'name' | 'hex' | 'image'>,
    value: string,
  ) => {
    setFormData((prev) => {
      const colors = [...prev.colors];
      const existing = colors[index] ?? { name: '', hex: '' };
      colors[index] = { ...existing, [key]: value } as ProductColor;
      return { ...prev, colors };
    });
  };

  const handleRemoveColor = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== index),
    }));
  };

  const handleColorImageUpload = async (index: number, file: File) => {
    setUploadingColorIndex(index);
    try {
      const result = await productsApi.uploadColorImage(file);
      if (result.success && result.image) {
        handleColorChange(index, 'image', result.image.url);
      }
    } catch (error) {
      console.error('Color image upload failed:', error);
      setFormError('Renk görseli yüklenemedi');
    } finally {
      setUploadingColorIndex(null);
    }
  };

  const handleAddSimilarProduct = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      similarProductIds: prev.similarProductIds.includes(id)
        ? prev.similarProductIds
        : [...prev.similarProductIds, id],
    }));
  };

  const handleRemoveSimilarProduct = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      similarProductIds: prev.similarProductIds.filter((item) => item !== id),
    }));
  };

  const handleMoveSimilarProduct = (id: string, direction: 'up' | 'down') => {
    setFormData((prev) => {
      const index = prev.similarProductIds.indexOf(id);
      if (index === -1) {
        return prev;
      }

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.similarProductIds.length) {
        return prev;
      }

      const similarProductIds = [...prev.similarProductIds];
      const [item] = similarProductIds.splice(index, 1);
      similarProductIds.splice(targetIndex, 0, item);

      return { ...prev, similarProductIds };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validation
    if (!formData.title.trim()) {
      setFormError('Ürün başlığı gerekli');
      return;
    }

    if (!formData.slug.trim()) {
      setFormError('Slug gerekli');
      return;
    }

    if (formData.categoryIds.length === 0) {
      setFormError('En az bir kategori seçin');
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      // Backend uses 'title' field
      formDataToSend.append('title', formData.title);
      formDataToSend.append('slug', formData.slug);
      if (formData.description) formDataToSend.append('description', formData.description);
      formDataToSend.append('categoryIds', JSON.stringify(formData.categoryIds));
      // Backend uses 'hidden' field
      // Send empty string for false (visible) - this consistently resolves to false in backend (via Transform or Boolean cast)
      // Send 'true' for true (hidden)
      formDataToSend.append('hidden', formData.hidden ? 'true' : '');
      // Same logic for featured to be safe
      formDataToSend.append('featured', formData.featured ? 'true' : '');

      // Dimensions - different structure for Set vs Chair/Table
      const isSet = formData.technicalSpecs.productType === 'set';

      const dims: Record<string, any> = {
        unit: formData.dimensions.unit,
      };

      if (isSet) {
        // For Sets: Use tableDimensions and chairDimensions
        const tableDims = formData.dimensions.tableDimensions;
        const chairDims = formData.dimensions.chairDimensions;

        if (tableDims.width || tableDims.height || tableDims.depth || tableDims.tableHeight) {
          dims.tableDimensions = {
            width: tableDims.width ? Number(tableDims.width) : undefined,
            height: tableDims.height ? Number(tableDims.height) : undefined,
            depth: tableDims.depth ? Number(tableDims.depth) : undefined,
            tableHeight: tableDims.tableHeight ? Number(tableDims.tableHeight) : undefined,
          };
        }

        if (chairDims.width || chairDims.height || chairDims.depth || chairDims.seatHeight) {
          dims.chairDimensions = {
            width: chairDims.width ? Number(chairDims.width) : undefined,
            height: chairDims.height ? Number(chairDims.height) : undefined,
            depth: chairDims.depth ? Number(chairDims.depth) : undefined,
            seatHeight: chairDims.seatHeight ? Number(chairDims.seatHeight) : undefined,
          };
        }
      } else {
        // For Chair/Table: Use primary dimensions
        dims.width = formData.dimensions.width ? Number(formData.dimensions.width) : undefined;
        dims.height = formData.dimensions.height ? Number(formData.dimensions.height) : undefined;
        dims.depth = formData.dimensions.depth ? Number(formData.dimensions.depth) : undefined;
        dims.seatHeight = formData.dimensions.seatHeight ? Number(formData.dimensions.seatHeight) : undefined;
        dims.tableHeight = formData.dimensions.tableHeight ? Number(formData.dimensions.tableHeight) : undefined;
      }

      formDataToSend.append('dimensions', JSON.stringify(dims));

      // Materials
      const materialsArray = formData.materials
        .split(',')
        .map((m) => m.trim())
        .filter(Boolean);
      formDataToSend.append('materials', JSON.stringify(materialsArray));

      // Technical Specs
      const techSpecs = {
        productType: formData.technicalSpecs.productType,
        capacity: formData.technicalSpecs.capacity || undefined,
        weight: formData.technicalSpecs.weight || undefined,
        usageArea: formData.technicalSpecs.usageArea || undefined,
        setContent: formData.technicalSpecs.setContent || undefined,
        tableCount: formData.technicalSpecs.tableCount || undefined,
        chairCount: formData.technicalSpecs.chairCount || undefined,
      };
      formDataToSend.append('technicalSpecs', JSON.stringify(techSpecs));

      if (formData.warranty) {
        formDataToSend.append('warranty', formData.warranty);
      }

      const colorsPayload = formData.colors
        .map((color) => ({
          name: color.name.trim(),
          hex: color.hex.trim(),
          ...(color.image ? { image: color.image.trim() } : {}),
        }))
        .filter((color) => color.name && color.hex);
      formDataToSend.append('colors', JSON.stringify(colorsPayload));

      formDataToSend.append('similarProductIds', JSON.stringify(formData.similarProductIds));

      // Existing images to keep (for update)
      if (editingProduct) {
        formDataToSend.append('existingImageIds', JSON.stringify(existingImages.map((img) => img.id)));
      }

      // New images
      imageFiles.forEach((file) => {
        formDataToSend.append('images', file);
      });

      if (editingProduct) {
        await productsApi.update(editingProduct.id, formDataToSend);
        toast.success('Ürün başarıyla güncellendi');
      } else {
        await productsApi.create(formDataToSend);
        toast.success('Ürün başarıyla eklendi');
      }

      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Bir hata oluştu');
      toast.error('İşlem sırasında bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await productsApi.delete(id);
      setDeleteConfirm(null);
      loadData();
      toast.success('Ürün başarıyla silindi');
    } catch (error) {
      toast.error('Ürün silinemedi');
    }
  };

  const toggleVisibility = async (id: string, currentHidden: boolean) => {
    try {
      await productsApi.updateVisibility(id, !currentHidden);
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, hidden: !currentHidden } : p))
      );
      toast.success(currentHidden ? 'Ürün görünür yapıldı' : 'Ürün gizlendi');
    } catch (error) {
      toast.error('Görünürlük güncellenemedi');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const totalImages = existingImages.length + imageFiles.length + files.length;

    if (totalImages > 10) {
      toast.warning('En fazla 10 resim yükleyebilirsiniz');
      return;
    }

    // Filter valid image types
    const validFiles = files.filter((file) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(file.type)
    );

    setImageFiles((prev) => [...prev, ...validFiles]);

    // Generate previews
    const newPreviews = validFiles.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeNewImage = (index: number) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (id: string) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const filteredProducts = products.filter(
    (p) =>
      p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPrimaryImage = (product: Product) => {
    const primary = product.images?.find((img) => img.isPrimary);
    return primary?.url || product.images?.[0]?.url;
  };

  // Bulk selection handlers
  const toggleSelectProduct = (id: string) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pId) => pId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map((p) => p.id));
    }
  };

  const handleBulkDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const count = selectedProducts.length;
      await productsApi.bulkDelete(selectedProducts);
      setBulkDeleteConfirm(false);
      setSelectedProducts([]);
      loadData();
      toast.success(`${count} ürün başarıyla silindi`);
    } catch (error) {
      toast.error('Toplu silme işlemi başarısız oldu');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ürünler</h1>
          <p className="text-sm text-gray-500 mt-1">
            Toplam {productMeta?.total ?? products.length} ürün
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Bulk Delete Button */}
          {selectedProducts.length > 0 && (
            <button
              onClick={() => setBulkDeleteConfirm(true)}
              className="h-10 px-4 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Seçilenleri Sil ({selectedProducts.length})
            </button>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 h-10 pl-10 pr-4 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
            />
          </div>

          {/* New Product Button */}
          <button
            onClick={openCreateModal}
            className="h-10 px-4 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Ürün
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
                  Resim
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Başlık
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                  Kategoriler
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                  Durum
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    {searchTerm ? 'Aramanızla eşleşen ürün bulunamadı' : 'Henüz ürün eklenmemiş'}
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product.id} className={cn("hover:bg-gray-50 transition-colors", selectedProducts.includes(product.id) && "bg-blue-50/30")}>
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleSelectProduct(product.id)}
                        className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                      />
                    </td>
                    {/* Image */}
                    <td className="px-4 py-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {getPrimaryImage(product) ? (
                          <img
                            src={getPrimaryImage(product)}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-gray-300" />
                        )}
                      </div>
                    </td>

                    {/* Title */}
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{product.title}</span>
                    </td>

                    {/* Slug */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                        {product.slug}
                      </code>
                    </td>

                    {/* Categories */}
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {product.categories?.slice(0, 2).map((cat) => (
                          <span
                            key={cat.id}
                            className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded"
                          >
                            {cat.name}
                          </span>
                        ))}
                        {(product.categories?.length || 0) > 2 && (
                          <span className="text-xs text-gray-400">
                            +{(product.categories?.length || 0) - 2}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Visibility Toggle */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleVisibility(product.id, product.hidden)}
                        className={cn(
                          'p-1.5 rounded-lg transition-colors',
                          !product.hidden
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        )}
                        title={product.hidden ? 'Görünür yap' : 'Gizle'}
                      >
                        {!product.hidden ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(product)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(product.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModalOpen(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[90vh] bg-white rounded-2xl shadow-xl z-50 overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
                </h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto">
                {creationStep === 'type-selection' && !editingProduct ? (
                  <div className="p-6 space-y-6">
                    <div className="text-center space-y-2">
                      <h3 className="text-lg font-semibold text-gray-900">Ürün Tipini Seçin</h3>
                      <p className="text-sm text-gray-500">Eklemek istediğiniz ürün tipine göre yapılandırma otomatik hazırlanacaktır.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        { id: 'chair', label: 'Sandalye', icon: Armchair, desc: 'Tekil sandalye satışı' },
                        { id: 'table', label: 'Masa', icon: TableIcon, desc: 'Tekil masa satışı' },
                        { id: 'set', label: 'Takım (Set)', icon: Layers, desc: 'Masa + Sandalye paketi' },
                      ].map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              technicalSpecs: {
                                ...prev.technicalSpecs,
                                productType: type.id as any,
                              },
                            }));
                            setCreationStep('form');
                          }}
                          className="flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-gray-100 hover:border-black hover:bg-gray-50 transition-all text-center group"
                        >
                          <type.icon className="w-10 h-10 text-gray-400 group-hover:text-black transition-colors" />
                          <div>
                            <span className="block font-bold text-gray-900">{type.label}</span>
                            <span className="text-xs text-gray-500">{type.desc}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Error Message */}
                    {formError && (
                      <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {formError}
                      </div>
                    )}

                    {/* Title */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700">
                        Ürün Başlığı <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                        placeholder="örnek: Elegance Yemek Sandalyesi"
                      />
                    </div>

                    {/* Slug */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700">
                        Slug <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm font-mono"
                        placeholder="elegance-yemek-sandalyesi"
                      />
                      <p className="text-xs text-gray-400">URL dostu benzersiz tanımlayıcı</p>
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700">Açıklama</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm resize-none"
                        placeholder="Ürün hakkında detaylı açıklama..."
                      />
                    </div>

                    {/* Categories */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700">
                        Kategoriler <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {categories.length === 0 ? (
                          <p className="text-sm text-gray-400">Henüz kategori yok</p>
                        ) : (
                          categories.map((cat) => (
                            <label
                              key={cat.id}
                              className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 px-2 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={formData.categoryIds.includes(cat.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({
                                      ...formData,
                                      categoryIds: [...formData.categoryIds, cat.id],
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      categoryIds: formData.categoryIds.filter((id) => id !== cat.id),
                                    });
                                  }
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                              />
                              <span className="text-sm text-gray-700">{cat.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {/* --- Product Type & Configuration (PRIMARY) --- */}
                    {/* Selected Configuration Display */}
                    {/* Configuration Display Removed per user request */}

                    {/* Unified Technical Details Section */}
                    <div className="space-y-6 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-2 border-b border-gray-100 pb-4 mb-4">
                        <Ruler className="w-5 h-5 text-gray-500" />
                        <h3 className="text-base font-bold text-gray-900 uppercase tracking-wide">Teknik Detaylar & Özellikler</h3>
                      </div>

                      {/* 1. Dimensions Group - Different for Set vs Chair/Table */}
                      {formData.technicalSpecs.productType === 'set' ? (
                        /* SET: Separate Table and Chair Dimensions */
                        <div className="space-y-6">
                          {/* Unit Selector (Shared) */}
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-bold text-gray-800">Ölçü Birimi</label>
                            <select
                              value={formData.dimensions.unit}
                              onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, unit: e.target.value } })}
                              className="w-24 h-9 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                            >
                              <option value="cm">cm</option>
                              <option value="mm">mm</option>
                            </select>
                          </div>

                          {/* Table Dimensions */}
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <label className="text-sm font-bold text-gray-800 mb-3 block">Masa Ölçüleri</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Genişlik</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.tableDimensions.width}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      tableDimensions: { ...formData.dimensions.tableDimensions, width: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Derinlik</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.tableDimensions.depth}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      tableDimensions: { ...formData.dimensions.tableDimensions, depth: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Yükseklik</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.tableDimensions.height}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      tableDimensions: { ...formData.dimensions.tableDimensions, height: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Masa Y.</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.tableDimensions.tableHeight}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      tableDimensions: { ...formData.dimensions.tableDimensions, tableHeight: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Chair Dimensions */}
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <label className="text-sm font-bold text-gray-800 mb-3 block">Sandalye Ölçüleri</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Genişlik</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.chairDimensions.width}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      chairDimensions: { ...formData.dimensions.chairDimensions, width: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Derinlik</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.chairDimensions.depth}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      chairDimensions: { ...formData.dimensions.chairDimensions, depth: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Yükseklik</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.chairDimensions.height}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      chairDimensions: { ...formData.dimensions.chairDimensions, height: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Oturum Y.</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.chairDimensions.seatHeight}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: {
                                      ...formData.dimensions,
                                      chairDimensions: { ...formData.dimensions.chairDimensions, seatHeight: e.target.value }
                                    }
                                  })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* CHAIR / TABLE: Single Dimensions */
                        <div className="space-y-3">
                          <label className="text-sm font-bold text-gray-800 flex items-center gap-2">
                            Ürün Boyutları
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {/* Width */}
                            <div>
                              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Genişlik</span>
                              <input
                                type="number"
                                value={formData.dimensions.width}
                                onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, width: e.target.value } })}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                                placeholder="Genişlik"
                                min="0"
                              />
                            </div>
                            {/* Height */}
                            <div>
                              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Yükseklik</span>
                              <input
                                type="number"
                                value={formData.dimensions.height}
                                onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, height: e.target.value } })}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                                placeholder="Yükseklik"
                                min="0"
                              />
                            </div>
                            {/* Depth */}
                            <div>
                              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Derinlik</span>
                              <input
                                type="number"
                                value={formData.dimensions.depth}
                                onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, depth: e.target.value } })}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                                placeholder="Derinlik"
                                min="0"
                              />
                            </div>
                            {/* Seat Height (Chair) */}
                            {formData.technicalSpecs.productType === 'chair' && (
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Oturum Y.</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.seatHeight}
                                  onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, seatHeight: e.target.value } })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                                  placeholder="Oturum Y."
                                  min="0"
                                />
                              </div>
                            )}
                            {/* Table Height (Table) */}
                            {formData.technicalSpecs.productType === 'table' && (
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Masa Y.</span>
                                <input
                                  type="number"
                                  value={formData.dimensions.tableHeight}
                                  onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, tableHeight: e.target.value } })}
                                  className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                                  placeholder="Masa Y."
                                  min="0"
                                />
                              </div>
                            )}
                            {/* Unit */}
                            <div>
                              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-1 block">Birim</span>
                              <select
                                value={formData.dimensions.unit}
                                onChange={(e) => setFormData({ ...formData, dimensions: { ...formData.dimensions, unit: e.target.value } })}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm bg-white"
                              >
                                <option value="cm">cm</option>
                                <option value="mm">mm</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 2. Specs & Materials */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-gray-100">

                        {/* Usage Area - Checkbox Group */}
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Kullanım Alanı</label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={formData.technicalSpecs.usageArea?.includes('İç Mekan') || false}
                                onChange={(e) => {
                                  const current = formData.technicalSpecs.usageArea || '';
                                  const areas = current.split(', ').filter(Boolean);
                                  let newAreas: string[];
                                  if (e.target.checked) {
                                    newAreas = [...areas.filter(a => a !== 'İç Mekan'), 'İç Mekan'];
                                  } else {
                                    newAreas = areas.filter(a => a !== 'İç Mekan');
                                  }
                                  newAreas.sort((a) => a === 'İç Mekan' ? -1 : 1);
                                  setFormData({
                                    ...formData,
                                    technicalSpecs: { ...formData.technicalSpecs, usageArea: newAreas.join(', ') }
                                  });
                                }}
                                className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                              />
                              <span className="text-sm font-medium text-gray-700 group-hover:text-black transition-colors">İç Mekan</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={formData.technicalSpecs.usageArea?.includes('Dış Mekan') || false}
                                onChange={(e) => {
                                  const current = formData.technicalSpecs.usageArea || '';
                                  const areas = current.split(', ').filter(Boolean);
                                  let newAreas: string[];
                                  if (e.target.checked) {
                                    newAreas = [...areas.filter(a => a !== 'Dış Mekan'), 'Dış Mekan'];
                                  } else {
                                    newAreas = areas.filter(a => a !== 'Dış Mekan');
                                  }
                                  newAreas.sort((a) => a === 'İç Mekan' ? -1 : 1);
                                  setFormData({
                                    ...formData,
                                    technicalSpecs: { ...formData.technicalSpecs, usageArea: newAreas.join(', ') }
                                  });
                                }}
                                className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                              />
                              <span className="text-sm font-medium text-gray-700 group-hover:text-black transition-colors">Dış Mekan</span>
                            </label>
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1.5">Tek veya her ikisini de seçebilirsiniz</p>
                        </div>

                        {/* Materials - Multi-Select Dropdown */}
                        <div className="relative">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Malzemeler</label>
                          <button
                            type="button"
                            onClick={() => setMaterialsDropdownOpen(!materialsDropdownOpen)}
                            className="w-full min-h-[40px] px-3 py-2 border border-gray-200 rounded-lg text-sm text-left focus:border-black focus:ring-1 focus:ring-black outline-none bg-white flex flex-wrap gap-1.5 items-center"
                          >
                            {selectedMaterials.length === 0 ? (
                              <span className="text-gray-400">Malzeme seçin...</span>
                            ) : (
                              selectedMaterials.map((material) => (
                                <span
                                  key={material}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-md"
                                >
                                  {material}
                                  <X
                                    className="w-3 h-3 hover:text-red-500 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMaterial(material);
                                    }}
                                  />
                                </span>
                              ))
                            )}
                          </button>

                          {/* Dropdown Panel */}
                          <AnimatePresence>
                            {materialsDropdownOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setMaterialsDropdownOpen(false)}
                                />
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                                >
                                  {Object.entries(MATERIAL_OPTIONS).map(([category, materials]) => (
                                    <div key={category}>
                                      <div className="px-3 py-2 bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider sticky top-0">
                                        {category}
                                      </div>
                                      <div className="py-1">
                                        {materials.map((material) => (
                                          <label
                                            key={material}
                                            className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedMaterials.includes(material)}
                                              onChange={() => toggleMaterial(material)}
                                              className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                                            />
                                            <span className="text-sm text-gray-700">{material}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </motion.div>
                              </>
                            )}
                          </AnimatePresence>
                        </div>

                        {/* Type Specific Fields */}
                        {formData.technicalSpecs.productType === 'chair' && (
                          <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Ürün Ağırlığı</label>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={formData.technicalSpecs.weight?.replace(/[^\d.]/g, '') || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    setFormData({
                                      ...formData,
                                      technicalSpecs: { ...formData.technicalSpecs, weight: value }
                                    });
                                  }
                                }}
                                className="w-full h-10 px-3 pr-10 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black outline-none"
                                placeholder="5.4"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                                kg
                              </span>
                            </div>
                          </div>
                        )}

                        {(formData.technicalSpecs.productType === 'table' || formData.technicalSpecs.productType === 'set') && (
                          <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Kişi Kapasitesi</label>
                            <input
                              type="text"
                              value={formData.technicalSpecs.capacity}
                              onChange={(e) => setFormData({ ...formData, technicalSpecs: { ...formData.technicalSpecs, capacity: e.target.value } })}
                              className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black outline-none"
                              placeholder="Örn: 6-8 Kişilik"
                            />
                          </div>
                        )}

                        {formData.technicalSpecs.productType === 'set' && (
                          <>
                            <div>
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Masa Adedi</label>
                              <input
                                type="number"
                                min="0"
                                value={formData.technicalSpecs.tableCount || ''}
                                onChange={(e) => setFormData({ ...formData, technicalSpecs: { ...formData.technicalSpecs, tableCount: parseInt(e.target.value) || 0 } })}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black outline-none"
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Sandalye Adedi</label>
                              <input
                                type="number"
                                min="0"
                                value={formData.technicalSpecs.chairCount || ''}
                                onChange={(e) => setFormData({ ...formData, technicalSpecs: { ...formData.technicalSpecs, chairCount: parseInt(e.target.value) || 0 } })}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black outline-none"
                                placeholder="0"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Renk Seçenekleri</label>
                        <p className="text-xs text-gray-400">
                          Müşteriye sunmak istediğiniz renk kartelasını ekleyin. Hex kodu ile birlikte açıklayıcı bir ad girin.
                        </p>
                      </div>

                      <div className="space-y-2">
                        {formData.colors.length === 0 && (
                          <div className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-4 py-3 bg-gray-50">
                            Henüz renk eklenmedi. Renk eklemek için aşağıdaki butonu kullanın.
                          </div>
                        )}

                        {formData.colors.map((color, index) => {
                          const previewHex = (() => {
                            const trimmed = color.hex?.trim();
                            if (!trimmed) return '#F3F4F6';
                            return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
                          })();

                          return (
                            <div
                              key={`${color.name || 'color'}-${index}`}
                              className="grid grid-cols-1 sm:grid-cols-[auto,1fr,1fr,auto] gap-3 items-center border border-gray-200 rounded-lg px-3 py-3"
                            >
                              <div className="relative">
                                <div
                                  className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform"
                                  style={{ backgroundColor: previewHex }}
                                  onClick={() => setActiveColorPicker(activeColorPicker === index ? null : index)}
                                />
                                {activeColorPicker === index && (
                                  <>
                                    <div className="fixed inset-0 z-40" onClick={() => setActiveColorPicker(null)} />
                                    <div className="absolute top-12 left-0 z-50 shadow-2xl rounded-xl p-3 bg-white border border-gray-100">
                                      <HexColorPicker color={previewHex} onChange={(newColor) => handleColorChange(index, 'hex', newColor)} />
                                    </div>
                                  </>
                                )}
                              </div>

                              <input
                                type="text"
                                value={color.name}
                                onChange={(e) => handleColorChange(index, 'name', e.target.value)}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                                placeholder="Renk Adı"
                              />
                              <input
                                type="text"
                                value={color.hex}
                                onChange={(e) => handleColorChange(index, 'hex', e.target.value)}
                                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm font-mono uppercase"
                                placeholder="#F5F5F5"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveColor(index)}
                                className="self-start p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                aria-label="Renk sil"
                              >
                                <X className="w-4 h-4" />
                              </button>

                              {/* Color Image Upload */}
                              <div className="sm:col-span-4 flex items-center gap-3">
                                <input
                                  type="file"
                                  id={`color-image-${index}`}
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleColorImageUpload(index, file);
                                    e.target.value = '';
                                  }}
                                />

                                {/* Preview or Upload Button */}
                                {color.image ? (
                                  <div className="flex items-center gap-3 flex-1">
                                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                                      <img
                                        src={color.image}
                                        alt={color.name || 'Renk görseli'}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-gray-500 truncate">Renk görseli yüklendi</p>
                                      <div className="flex gap-2 mt-1">
                                        <label
                                          htmlFor={`color-image-${index}`}
                                          className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer"
                                        >
                                          Değiştir
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => handleColorChange(index, 'image', '')}
                                          className="text-xs text-red-500 hover:text-red-600"
                                        >
                                          Kaldır
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <label
                                    htmlFor={`color-image-${index}`}
                                    className={cn(
                                      "flex-1 flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
                                      uploadingColorIndex === index
                                        ? "border-gray-300 bg-gray-50"
                                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                    )}
                                  >
                                    {uploadingColorIndex === index ? (
                                      <>
                                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                        <span className="text-sm text-gray-400">Yükleniyor...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-500">Renk görseli yükle</span>
                                      </>
                                    )}
                                  </label>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={handleAddColor}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Renk Ekle
                      </button>
                    </div>

                    {/* Similar Products */}
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Benzer Ürünler</label>
                        <p className="text-xs text-gray-400">
                          Seçili ürünler ürün detay sayfasında "Benzer Ürünler" bölümünde listelenir. Sıralama yukarıdaki sıraya göre yapılır.
                        </p>
                      </div>

                      {selectedSimilarProducts.length > 0 && (
                        <div className="space-y-2">
                          {selectedSimilarProducts.map((product, index) => (
                            <div
                              key={product.id}
                              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
                            >
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-800">{product.title}</p>
                                <p className="text-xs text-gray-400">#{index + 1} • {product.slug}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleMoveSimilarProduct(product.id, 'up')}
                                  disabled={index === 0}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                                  aria-label="Yukarı taşı"
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleMoveSimilarProduct(product.id, 'down')}
                                  disabled={index === selectedSimilarProducts.length - 1}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                                  aria-label="Aşağı taşı"
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSimilarProduct(product.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  aria-label="Benzer üründen çıkar"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={similarSearch}
                            onChange={(e) => setSimilarSearch(e.target.value)}
                            className="w-full h-10 pl-10 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-sm"
                            placeholder="Ürün ara..."
                          />
                        </div>

                        <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
                          {availableSimilarProducts.length === 0 ? (
                            <p className="text-sm text-gray-400 px-1 py-2">
                              {products.length <= 1
                                ? 'Listeye ekleyebileceğiniz başka ürün bulunmuyor.'
                                : 'Eşleşen ürün bulunamadı.'}
                            </p>
                          ) : (
                            availableSimilarProducts.map((product) => (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => handleAddSimilarProduct(product.id)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors"
                              >
                                <div>
                                  <p className="text-sm font-medium text-gray-700">{product.title}</p>
                                  <p className="text-xs text-gray-400">{product.slug}</p>
                                </div>
                                <Plus className="w-4 h-4 text-gray-400" />
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Images */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-gray-700">
                        Resimler (maks. 10)
                      </label>

                      {/* Existing Images */}
                      {existingImages.length > 0 && (
                        <div className="grid grid-cols-5 gap-2">
                          {existingImages.map((image) => (
                            <div
                              key={image.id}
                              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
                            >
                              <img
                                src={image.url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                              {image.isPrimary && (
                                <div className="absolute bottom-1 left-1 bg-yellow-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                                  Ana
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removeExistingImage(image.id)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* New Image Previews */}
                      {imagePreviews.length > 0 && (
                        <div className="grid grid-cols-5 gap-2">
                          {imagePreviews.map((preview, index) => (
                            <div
                              key={index}
                              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
                            >
                              <img
                                src={preview}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute bottom-1 left-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                                Yeni
                              </div>
                              <button
                                type="button"
                                onClick={() => removeNewImage(index)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload Button */}
                      {existingImages.length + imagePreviews.length < 10 && (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors">
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Resim yüklemek için tıklayın</span>
                          <span className="text-xs text-gray-400 mt-1">
                            JPG, PNG, WebP (maks. {10 - existingImages.length - imagePreviews.length} resim)
                          </span>
                          <input
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/webp,image/avif"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </form>
                )}
              </div>

              {/* Modal Footer */}
              {!(creationStep === 'type-selection' && !editingProduct) && (
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="h-10 px-4 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={saving}
                    className="h-10 px-6 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingProduct ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDeleteConfirm(null)}
              className="fixed inset-0 bg-black/50 z-40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl shadow-xl z-50 p-6"
            >
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ürünü Sil</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bu ürünü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="h-10 px-4 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirm)}
                    className="h-10 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Evet, Sil
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bulk Delete Confirmation Modal */}
      <AnimatePresence>
        {bulkDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setBulkDeleteConfirm(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl shadow-xl z-50 p-6"
            >
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Seçilenleri Sil</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Seçilen {selectedProducts.length} ürünü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setBulkDeleteConfirm(false)}
                    className="h-10 px-4 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                    disabled={isDeleting}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="h-10 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    disabled={isDeleting}
                  >
                    {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Evet, Sil
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsPage;
